import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";

// services
import taskService, { TASK_STATUS } from "services/tasks.js";
import projectService from "services/projects.js";
import aiModelService from "services/aiModels.js";
import logger from "utils/logger";

export default function CardTaskForm({ task, onSave, onCancel, isOpen }) {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    status: 'pending',
    priority: 'medium',
    project_code: '',
    agent: '',
    auto_merge: false,
    model_id: ''
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [projects, setProjects] = useState([]);
  const [aiModels, setAiModels] = useState([]);
  const [responses, setResponses] = useState([]);
  const [newResponse, setNewResponse] = useState('');
  const [responseLoading, setResponseLoading] = useState(false);
  const [responseError, setResponseError] = useState('');
  const [relations, setRelations] = useState([]);
  const [availableTasks, setAvailableTasks] = useState([]);
  const [selectedRelatedTask, setSelectedRelatedTask] = useState('');
  const [relationLoading, setRelationLoading] = useState(false);
  const [relationError, setRelationError] = useState('');
  const [showRejectionDialog, setShowRejectionDialog] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [rejectionReasonError, setRejectionReasonError] = useState('');
  const [pendingStatusChange, setPendingStatusChange] = useState(null);
  const modalContentRef = React.useRef(null);
  const fieldRefs = React.useRef({
    title: null,
    description: null,
    project_code: null
  });

  // Reset form when task changes or modal opens
  useEffect(() => {
    if (isOpen) {
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';

      if (task) {
        // Edit mode
        setFormData({
          title: task.title || '',
          description: task.description || '',
          status: task.status || 'pending',
          priority: task.priority || 'medium',
          project_code: task.project_code || '',
          agent: task.agent || '',
          auto_merge: task.auto_merge || false,
          model_id: task.ai_model ? task.model_id : ''
        });
      } else {
        // Create mode
        setFormData({
          title: '',
          description: '',
          status: 'pending',
          priority: 'medium',
          project_code: '',
          agent: '',
          auto_merge: false,
          model_id: ''
        });
      }
      setErrors({});
    } else {
      // Restore body scroll when modal is closed
      document.body.style.overflow = 'unset';
    }

    // Cleanup function
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [task, isOpen]);

  // Handle ESC key press to close modal
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && isOpen) {
        handleCancel();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isOpen]);

  // Load projects and AI models when component mounts
  useEffect(() => {
    const loadProjects = async () => {
      try {
        const response = await projectService.getProjects();
        setProjects(response.data || response.projects || []);
      } catch (err) {
        logger.error('Error loading projects:', err);
        setProjects([]);
      }
    };

    const loadAiModels = async () => {
      try {
        const response = await aiModelService.getActiveModels();
        setAiModels(response.data || []);
      } catch (err) {
        logger.error('Error loading AI models:', err);
        setAiModels([]);
      }
    };

    loadProjects();
    loadAiModels();
  }, []);

  // Load responses when task changes
  useEffect(() => {
    const loadResponses = async () => {
      if (task && task.id) {
        try {
          // Check if task already has responses (from getTask API call)
          if (task.responses) {
            setResponses(task.responses);
          } else {
            // Otherwise fetch them
            const response = await taskService.getTaskResponses(task.id);
            setResponses(response.responses || []);
          }
        } catch (err) {
          logger.error('Error loading responses:', err);
          setResponses([]);
        }
      } else {
        setResponses([]);
      }
      setNewResponse('');
      setResponseError('');
    };

    if (isOpen) {
      loadResponses();
    }
  }, [task, isOpen]);

  // Load relations and available tasks when task changes
  useEffect(() => {
    const loadRelationsAndTasks = async () => {
      if (task && task.id) {
        try {
          // Use relations that come with the task object (no need for additional API call)
          const taskRelations = task.relations || [];
          setRelations(taskRelations);

          // Load all available tasks for this project (excluding current task and finished tasks)
          // Only show tasks that are not finished (created, pending, working, reviewing, failed)
          const tasksResponse = await taskService.getTasks({
            project_code: task.project_code,
            include_disabled: false,
            status: [
              TASK_STATUS.CREATED,
              TASK_STATUS.PENDING,
              TASK_STATUS.WORKING,
              TASK_STATUS.REVIEWING,
              TASK_STATUS.FAILED
            ]
          });
          const allTasks = tasksResponse.data || tasksResponse.tasks || [];
          // Filter out current task and already related tasks
          const relatedTaskIds = taskRelations.map(r => r.related_task_id);
          const available = allTasks.filter(t =>
            t.id !== task.id && !relatedTaskIds.includes(t.id)
          );
          setAvailableTasks(available);
        } catch (err) {
          logger.error('Error loading tasks:', err);
          setRelations([]);
          setAvailableTasks([]);
        }
      } else {
        setRelations([]);
        setAvailableTasks([]);
      }
      setSelectedRelatedTask('');
      setRelationError('');
    };

    if (isOpen) {
      loadRelationsAndTasks();
    }
  }, [task, isOpen]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    // When status changes to 'rechazada', show rejection reason dialog
    if (name === 'status' && value === 'rechazada') {
      setPendingStatusChange(value);
      setRejectionReason('');
      setRejectionReasonError('');
      setShowRejectionDialog(true);
      return;
    }

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleRejectionConfirm = () => {
    if (!rejectionReason.trim()) {
      setRejectionReasonError('El motivo del rechazo es obligatorio');
      return;
    }
    // Apply the status change and store the rejection reason
    setFormData(prev => ({ ...prev, status: pendingStatusChange }));
    setShowRejectionDialog(false);
    setPendingStatusChange(null);
  };

  const handleRejectionCancel = () => {
    setShowRejectionDialog(false);
    setPendingStatusChange(null);
    setRejectionReason('');
    setRejectionReasonError('');
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'El título es obligatorio';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'La descripción es obligatoria';
    }

    if (!formData.project_code || !formData.project_code.trim()) {
      newErrors.project_code = 'El proyecto es obligatorio';
    }

    // Scroll to top of modal and focus first field with error if there are errors
    if (Object.keys(newErrors).length > 0) {
      if (modalContentRef.current) {
        modalContentRef.current.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }

      // Focus first field with error for accessibility
      const firstErrorField = Object.keys(newErrors)[0];
      if (fieldRefs.current[firstErrorField]) {
        setTimeout(() => {
          fieldRefs.current[firstErrorField]?.focus();
        }, 300); // Wait for scroll animation
      }
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);
    try {
      // Prepare data - convert empty model_id to null
      const submitData = {
        ...formData,
        model_id: formData.model_id ? parseInt(formData.model_id) : null
      };

      let result;
      if (task) {
        // Update existing task
        result = await taskService.updateTask(task.id, submitData);
      } else {
        // Create new task
        result = await taskService.createTask(submitData);
      }

      // If task was set to 'rechazada' and a rejection reason was provided, add it as a comment
      if (formData.status === 'rechazada' && rejectionReason.trim()) {
        const savedTask = result.task || result.data || result;
        const taskId = savedTask?.id || task?.id;
        if (taskId) {
          try {
            const comment = await taskService.createTaskResponse(taskId, {
              content: `MOTIVO DEL RECHAZO: ${rejectionReason.trim()}`
            });
            if (comment.response) {
              setResponses(prev => [...prev, comment.response]);
            }
          } catch (commentError) {
            logger.error('Error adding rejection comment:', commentError);
            // Don't block the save flow if the comment fails
          }
        }
        setRejectionReason('');
      }

      onSave(result);
    } catch (error) {
      logger.error('Error saving task:', error);

      // Handle validation errors from API
      if (error.response?.status === 422 && error.response?.data?.messages) {
        setErrors(error.response.data.messages);
      } else {
        setErrors({
          general: 'Error al guardar la tarea. Por favor, intenta nuevamente.'
        });
      }

      // Scroll to top of modal to show errors and focus first error field
      if (modalContentRef.current) {
        modalContentRef.current.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }

      // Focus first field with error for accessibility
      const firstErrorField = Object.keys(errors)[0];
      if (fieldRefs.current[firstErrorField]) {
        setTimeout(() => {
          fieldRefs.current[firstErrorField]?.focus();
        }, 300); // Wait for scroll animation
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      title: '',
      description: '',
      status: 'pending',
      priority: 'medium',
      project_code: '',
      agent: '',
      auto_merge: false,
      model_id: ''
    });
    setErrors({});
    setResponses([]);
    setNewResponse('');
    setResponseError('');
    setRejectionReason('');
    setRejectionReasonError('');
    setShowRejectionDialog(false);
    setPendingStatusChange(null);
    onCancel();
  };

  const handleResponseSubmit = async (e) => {
    e.preventDefault();

    if (!newResponse.trim()) {
      setResponseError('El comentario no puede estar vacío');
      return;
    }

    if (!task || !task.id) {
      setResponseError('No se puede agregar comentario sin una tarea guardada');
      return;
    }

    setResponseLoading(true);
    setResponseError('');

    try {
      const result = await taskService.createTaskResponse(task.id, {
        content: newResponse.trim()
      });

      // Add the new response to the list
      if (result.response) {
        setResponses(prev => [...prev, result.response]);
      }

      // Clear the form
      setNewResponse('');
    } catch (error) {
      logger.error('Error creating response:', error);
      setResponseError('Error al agregar comentario. Por favor, intenta nuevamente.');
    } finally {
      setResponseLoading(false);
    }
  };

  const handleAddRelation = async () => {
    if (!selectedRelatedTask) {
      setRelationError('Por favor selecciona una tarea');
      return;
    }

    if (!task || !task.id) {
      setRelationError('No se puede agregar relación sin una tarea guardada');
      return;
    }

    setRelationLoading(true);
    setRelationError('');

    try {
      const result = await taskService.createTaskRelation(task.id, {
        related_task_id: parseInt(selectedRelatedTask),
        relation_type: 'blocks'
      });

      // Add the new relation to the list
      if (result.relation) {
        setRelations(prev => [...prev, result.relation]);
        // Remove the added task from available tasks
        setAvailableTasks(prev => prev.filter(t => t.id !== parseInt(selectedRelatedTask)));
      }

      // Clear the form
      setSelectedRelatedTask('');
    } catch (error) {
      logger.error('Error creating relation:', error);
      if (error.response?.data?.error) {
        setRelationError(error.response.data.error);
      } else {
        setRelationError('Error al agregar tarea predecesora. Por favor, intenta nuevamente.');
      }
    } finally {
      setRelationLoading(false);
    }
  };

  const handleRemoveRelation = async (relationId, relatedTask) => {
    if (!task || !task.id) {
      return;
    }

    try {
      await taskService.deleteTaskRelation(task.id, relationId);

      // Remove the relation from the list
      setRelations(prev => prev.filter(r => r.id !== relationId));
      // Add the task back to available tasks
      if (relatedTask) {
        setAvailableTasks(prev => [...prev, relatedTask]);
      }
    } catch (error) {
      logger.error('Error deleting relation:', error);
      setRelationError('Error al eliminar tarea predecesora. Por favor, intenta nuevamente.');
    }
  };

  if (!isOpen) return null;

  // Agregar estilos CSS para animación
  const spinnerStyle = `
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
  `;

  return createPortal(
    <>
      <style>{spinnerStyle}</style>

      {/* Rejection Reason Dialog */}
      {showRejectionDialog && (
        <div
          style={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            zIndex: 200000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1rem'
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '0.5rem',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
              width: '100%',
              maxWidth: '480px',
              padding: '1.5rem'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem', gap: '0.75rem' }}>
              <div style={{
                width: '2.5rem', height: '2.5rem',
                borderRadius: '50%',
                backgroundColor: '#fce7f3',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0
              }}>
                <span style={{ fontSize: '1.25rem' }}>🚫</span>
              </div>
              <div>
                <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '700', color: '#111827' }}>
                  Rechazar tarea
                </h3>
                <p style={{ margin: 0, fontSize: '0.8rem', color: '#6b7280' }}>
                  Indica el motivo del rechazo para notificar a los agentes
                </p>
              </div>
            </div>

            {/* Rejection reason textarea */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={{
                display: 'block',
                fontSize: '0.875rem',
                fontWeight: '600',
                color: '#374151',
                marginBottom: '0.5rem'
              }}>
                Motivo del rechazo *
              </label>
              <textarea
                value={rejectionReason}
                onChange={(e) => {
                  setRejectionReason(e.target.value);
                  if (e.target.value.trim()) setRejectionReasonError('');
                }}
                placeholder="Explica por qué se rechaza esta tarea y qué deben evitar los agentes en el futuro..."
                rows={4}
                autoFocus
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: rejectionReasonError ? '1px solid #dc2626' : '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem',
                  resize: 'vertical',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />
              {rejectionReasonError && (
                <p style={{ color: '#dc2626', fontSize: '0.75rem', marginTop: '0.25rem' }}>
                  {rejectionReasonError}
                </p>
              )}
              <p style={{ color: '#6b7280', fontSize: '0.75rem', marginTop: '0.35rem' }}>
                Se guardará como comentario con el prefijo "MOTIVO DEL RECHAZO"
              </p>
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
              <button
                type="button"
                onClick={handleRejectionCancel}
                style={{
                  padding: '0.5rem 1rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  color: '#374151',
                  backgroundColor: 'white',
                  cursor: 'pointer'
                }}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleRejectionConfirm}
                style={{
                  padding: '0.5rem 1.25rem',
                  border: 'none',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  color: 'white',
                  backgroundColor: '#be185d',
                  cursor: 'pointer'
                }}
              >
                Confirmar rechazo
              </button>
            </div>
          </div>
        </div>
      )}

    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        zIndex: 99999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem'
      }}
    >
      <div
        ref={modalContentRef}
        style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          width: '100%',
          maxWidth: '800px',
          maxHeight: '90vh',
          overflowY: 'auto',
          zIndex: 100000,
          boxShadow: '0 10px 25px rgba(0, 0, 0, 0.5)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          padding: '1.5rem 1.5rem 1rem',
          borderBottom: '1px solid #e5e7eb'
        }}>
          <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 'bold', color: '#374151' }}>
            {task ? 'Editar Tarea' : 'Nueva Tarea'}
          </h3>
          <button
            onClick={handleCancel}
            style={{ 
              background: 'none', 
              border: 'none', 
              fontSize: '1.5rem', 
              cursor: 'pointer',
              color: '#6b7280',
              padding: '0.25rem'
            }}
          >
            ×
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={{ padding: '1.5rem' }}>
          {errors.general && (
            <div style={{
              marginBottom: '1rem',
              padding: '0.75rem',
              backgroundColor: '#fef2f2',
              border: '1px solid #fecaca',
              color: '#dc2626',
              borderRadius: '0.375rem'
            }}>
              {errors.general}
            </div>
          )}

          {/* Title */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ 
              display: 'block', 
              color: '#374151', 
              fontSize: '0.875rem', 
              fontWeight: 'bold', 
              marginBottom: '0.5rem' 
            }}>
              Título *
            </label>
            <input
              ref={(el) => fieldRefs.current.title = el}
              type="text"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              placeholder="Ingrese el título de la tarea"
              disabled={loading}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: errors.title ? '1px solid #dc2626' : '1px solid #d1d5db',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                outline: 'none',
                transition: 'border-color 0.15s ease-in-out',
                backgroundColor: loading ? '#f3f4f6' : 'white'
              }}
            />
            {errors.title && (
              <p style={{ color: '#dc2626', fontSize: '0.75rem', marginTop: '0.25rem' }}>
                {errors.title}
              </p>
            )}
          </div>

          {/* Creation and modification dates - only show when editing */}
          {task && (task.created_at || task.updated_at) && (
            <div style={{ 
              marginBottom: '1rem',
              padding: '1rem',
              backgroundColor: '#f9fafb',
              border: '1px solid #e5e7eb',
              borderRadius: '0.375rem'
            }}>
              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                {task.created_at && (
                  <div style={{ flex: '1', minWidth: '200px' }}>
                    <label style={{ 
                      display: 'block', 
                      color: '#6b7280', 
                      fontSize: '0.75rem', 
                      fontWeight: 'bold', 
                      marginBottom: '0.25rem' 
                    }}>
                      Fecha de creación
                    </label>
                    <p style={{ 
                      margin: 0, 
                      fontSize: '0.875rem', 
                      color: '#374151',
                      fontFamily: 'monospace'
                    }}>
                      {new Date(task.created_at).toLocaleString('es-ES', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit'
                      })}
                    </p>
                  </div>
                )}
                
                {task.updated_at && (
                  <div style={{ flex: '1', minWidth: '200px' }}>
                    <label style={{ 
                      display: 'block', 
                      color: '#6b7280', 
                      fontSize: '0.75rem', 
                      fontWeight: 'bold', 
                      marginBottom: '0.25rem' 
                    }}>
                      Última modificación
                    </label>
                    <p style={{ 
                      margin: 0, 
                      fontSize: '0.875rem', 
                      color: '#374151',
                      fontFamily: 'monospace'
                    }}>
                      {new Date(task.updated_at).toLocaleString('es-ES', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit'
                      })}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Description */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ 
              display: 'block', 
              color: '#374151', 
              fontSize: '0.875rem', 
              fontWeight: 'bold', 
              marginBottom: '0.5rem' 
            }}>
              Descripción *
            </label>
            <textarea
              ref={(el) => fieldRefs.current.description = el}
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows="4"
              placeholder="Ingrese la descripción de la tarea"
              disabled={loading}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: errors.description ? '1px solid #dc2626' : '1px solid #d1d5db',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                outline: 'none',
                resize: 'vertical',
                minHeight: '100px',
                transition: 'border-color 0.15s ease-in-out',
                backgroundColor: loading ? '#f3f4f6' : 'white'
              }}
            />
            {errors.description && (
              <p style={{ color: '#dc2626', fontSize: '0.75rem', marginTop: '0.25rem' }}>
                {errors.description}
              </p>
            )}
          </div>

          {/* Agent */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ 
              display: 'block', 
              color: '#374151', 
              fontSize: '0.875rem', 
              fontWeight: 'bold', 
              marginBottom: '0.5rem' 
            }}>
              Agente <small style={{ color: '#6b7280', fontWeight: 'normal' }}>(opcional)</small>
            </label>
            <input
              type="text"
              name="agent"
              value={formData.agent}
              onChange={handleInputChange}
              placeholder="Nombre del agente IA que creó la tarea (ej: GPT-4, Claude, etc.)"
              disabled={loading}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: errors.agent ? '1px solid #dc2626' : '1px solid #d1d5db',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                outline: 'none',
                transition: 'border-color 0.15s ease-in-out',
                backgroundColor: loading ? '#f3f4f6' : 'white'
              }}
            />
            {errors.agent && (
              <p style={{ color: '#dc2626', fontSize: '0.75rem', marginTop: '0.25rem' }}>
                {errors.agent}
              </p>
            )}
          </div>

          {/* AI Model */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{
              display: 'block',
              color: '#374151',
              fontSize: '0.875rem',
              fontWeight: 'bold',
              marginBottom: '0.5rem'
            }}>
              Modelo de IA <small style={{ color: '#6b7280', fontWeight: 'normal' }}>(opcional)</small>
            </label>
            {task && task.ai_model ? (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                marginBottom: '0.5rem'
              }}>
                <span style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '0.375rem 0.75rem',
                  backgroundColor: '#f0fdf4',
                  border: '1px solid #86efac',
                  borderRadius: '0.375rem',
                  fontSize: '0.8125rem',
                  color: '#166534',
                  fontWeight: '500'
                }}>
                  <i className="fas fa-brain" style={{ marginRight: '0.375rem', fontSize: '0.75rem' }}></i>
                  {task.ai_model.name}
                  {task.ai_model.provider && (
                    <span style={{ color: '#6b7280', marginLeft: '0.375rem', fontWeight: 'normal' }}>
                      ({task.ai_model.provider})
                    </span>
                  )}
                </span>
              </div>
            ) : null}
            <select
              name="model_id"
              value={formData.model_id}
              onChange={handleInputChange}
              disabled={loading}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: errors.model_id ? '1px solid #dc2626' : '1px solid #d1d5db',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                outline: 'none',
                backgroundColor: loading ? '#f3f4f6' : 'white'
              }}
            >
              <option value="">Sin modelo asignado</option>
              {aiModels.map(m => (
                <option key={m.id} value={m.id}>
                  {m.name}{m.provider ? ` (${m.provider})` : ''}
                </option>
              ))}
            </select>
            {errors.model_id && (
              <p style={{ color: '#dc2626', fontSize: '0.75rem', marginTop: '0.25rem' }}>
                {errors.model_id}
              </p>
            )}
          </div>

          {/* Status, Priority and Project */}
          <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem' }}>
            <div style={{ flex: 1 }}>
              <label style={{ 
                display: 'block', 
                color: '#374151', 
                fontSize: '0.875rem', 
                fontWeight: 'bold', 
                marginBottom: '0.5rem' 
              }}>
                Estado
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem',
                  outline: 'none',
                  backgroundColor: loading ? '#f3f4f6' : 'white'
                }}
              >
                <option value="created">Creada</option>
                <option value="pending">Pendiente</option>
                <option value="working">En Curso</option>
                <option value="reviewing">En Revisión</option>
                <option value="finished">Finalizada</option>
                <option value="failed">Fallida</option>
                <option value="rechazada">Rechazada</option>
              </select>
              {formData.status === 'rechazada' && rejectionReason && (
                <p style={{ color: '#9d174d', fontSize: '0.72rem', marginTop: '0.3rem' }}>
                  ✓ Motivo registrado — se guardará como comentario
                </p>
              )}
            </div>

            <div style={{ flex: 1 }}>
              <label style={{ 
                display: 'block', 
                color: '#374151', 
                fontSize: '0.875rem', 
                fontWeight: 'bold', 
                marginBottom: '0.5rem' 
              }}>
                Prioridad
              </label>
              <select
                name="priority"
                value={formData.priority}
                onChange={handleInputChange}
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem',
                  outline: 'none',
                  backgroundColor: loading ? '#f3f4f6' : 'white'
                }}
              >
                <option value="low">Baja</option>
                <option value="medium">Media</option>
                <option value="high">Alta</option>
                <option value="urgent">Urgente</option>
              </select>
            </div>

            <div style={{ flex: 1 }}>
              <label style={{ 
                display: 'block', 
                color: '#374151', 
                fontSize: '0.875rem', 
                fontWeight: 'bold', 
                marginBottom: '0.5rem' 
              }}>
                Proyecto *
              </label>
              <select
                ref={(el) => fieldRefs.current.project_code = el}
                name="project_code"
                value={formData.project_code}
                onChange={handleInputChange}
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: errors.project_code ? '1px solid #dc2626' : '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem',
                  outline: 'none',
                  backgroundColor: loading ? '#f3f4f6' : 'white'
                }}
              >
                <option value="">Seleccionar proyecto...</option>
                {projects.map(project => (
                  <option key={project.project_code} value={project.project_code}>
                    {project.project_name}
                  </option>
                ))}
              </select>
              {errors.project_code && (
                <p style={{ color: '#dc2626', fontSize: '0.75rem', marginTop: '0.25rem' }}>
                  {errors.project_code}
                </p>
              )}
            </div>
          </div>

          {/* Auto Merge */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              cursor: 'pointer',
              userSelect: 'none'
            }}>
              <input
                type="checkbox"
                name="auto_merge"
                checked={formData.auto_merge}
                onChange={handleInputChange}
                disabled={loading}
                style={{
                  width: '1.125rem',
                  height: '1.125rem',
                  cursor: loading ? 'not-allowed' : 'pointer'
                }}
              />
              <span style={{
                color: '#374151',
                fontSize: '0.875rem',
                fontWeight: 'bold'
              }}>
                Auto-merge
              </span>
              <span style={{
                color: '#6b7280',
                fontSize: '0.75rem',
                fontWeight: 'normal'
              }}>
                (fusionar automáticamente los cambios cuando estén listos)
              </span>
            </label>
          </div>

          {/* Task Relations Section - Only show when editing an existing task */}
          {task && task.id && (
            <div style={{
              marginBottom: '1.5rem',
              marginTop: '1.5rem',
              paddingTop: '1.5rem',
              borderTop: '2px solid #e5e7eb'
            }}>
              <h4 style={{
                fontSize: '1rem',
                fontWeight: 'bold',
                color: '#374151',
                marginBottom: '1rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <span>🔗</span> Tareas Predecesoras ({relations.length})
              </h4>

              {/* Relations List */}
              <div style={{
                marginBottom: '1rem'
              }}>
                {relations.length === 0 ? (
                  <div style={{
                    padding: '1rem',
                    backgroundColor: '#f9fafb',
                    border: '1px dashed #d1d5db',
                    borderRadius: '0.375rem',
                    textAlign: 'center',
                    color: '#6b7280',
                    fontSize: '0.875rem'
                  }}>
                    No hay tareas predecesoras. Esta tarea puede ejecutarse sin bloqueos.
                  </div>
                ) : (
                  relations.map((relation, index) => (
                    <div
                      key={relation.id}
                      style={{
                        padding: '0.75rem',
                        backgroundColor: '#fff',
                        border: '1px solid #e5e7eb',
                        borderRadius: '0.375rem',
                        marginBottom: index < relations.length - 1 ? '0.75rem' : '0',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <div style={{
                          fontSize: '0.875rem',
                          fontWeight: 'bold',
                          color: '#374151',
                          marginBottom: '0.25rem'
                        }}>
                          {relation.related_task?.title || `Tarea #${relation.related_task_id}`}
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                          {relation.related_task?.status && (
                            <span style={{
                              fontSize: '0.75rem',
                              padding: '0.125rem 0.5rem',
                              backgroundColor: relation.related_task.status === 'finished' ? '#10b981' : '#f59e0b',
                              color: 'white',
                              borderRadius: '0.25rem',
                              fontWeight: 'bold'
                            }}>
                              {relation.related_task.status === 'finished' ? '✓ Finalizada' :
                               relation.related_task.status === 'working' ? 'En Curso' :
                               relation.related_task.status === 'reviewing' ? 'En Revisión' :
                               relation.related_task.status === 'pending' ? 'Pendiente' :
                               relation.related_task.status === 'created' ? 'Creada' : 'Fallida'}
                            </span>
                          )}
                          {relation.related_task?.priority && (
                            <span style={{
                              fontSize: '0.75rem',
                              padding: '0.125rem 0.5rem',
                              backgroundColor: '#e5e7eb',
                              color: '#374151',
                              borderRadius: '0.25rem'
                            }}>
                              {relation.related_task.priority === 'urgent' ? '🔴 Urgente' :
                               relation.related_task.priority === 'high' ? '🟠 Alta' :
                               relation.related_task.priority === 'medium' ? '🟡 Media' : '🔵 Baja'}
                            </span>
                          )}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveRelation(relation.id, relation.related_task)}
                        style={{
                          padding: '0.5rem 0.75rem',
                          backgroundColor: '#dc2626',
                          color: 'white',
                          border: 'none',
                          borderRadius: '0.375rem',
                          fontSize: '0.75rem',
                          cursor: 'pointer',
                          fontWeight: 'bold',
                          transition: 'background-color 0.15s',
                          marginLeft: '1rem'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#b91c1c'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = '#dc2626'}
                      >
                        ✕ Eliminar
                      </button>
                    </div>
                  ))
                )}
              </div>

              {/* Add New Relation */}
              <div>
                <div style={{ marginBottom: '0.75rem' }}>
                  <label style={{
                    display: 'block',
                    color: '#374151',
                    fontSize: '0.875rem',
                    fontWeight: 'bold',
                    marginBottom: '0.5rem'
                  }}>
                    Agregar Tarea Predecesora
                  </label>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <select
                      value={selectedRelatedTask}
                      onChange={(e) => {
                        setSelectedRelatedTask(e.target.value);
                        setRelationError('');
                      }}
                      disabled={relationLoading || availableTasks.length === 0}
                      style={{
                        flex: 1,
                        padding: '0.75rem',
                        border: relationError ? '1px solid #dc2626' : '1px solid #d1d5db',
                        borderRadius: '0.375rem',
                        fontSize: '0.875rem',
                        outline: 'none',
                        backgroundColor: (relationLoading || availableTasks.length === 0) ? '#f3f4f6' : 'white'
                      }}
                    >
                      <option value="">
                        {availableTasks.length === 0 ? 'No hay tareas disponibles' : 'Seleccionar tarea...'}
                      </option>
                      {availableTasks.map(t => (
                        <option key={t.id} value={t.id}>
                          #{t.id} - {t.title} ({t.status})
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={handleAddRelation}
                      disabled={relationLoading || !selectedRelatedTask}
                      style={{
                        padding: '0.75rem 1.5rem',
                        backgroundColor: (relationLoading || !selectedRelatedTask) ? '#9ca3af' : '#3b82f6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '0.375rem',
                        fontSize: '0.875rem',
                        fontWeight: 'bold',
                        cursor: (relationLoading || !selectedRelatedTask) ? 'not-allowed' : 'pointer',
                        transition: 'background-color 0.15s',
                        whiteSpace: 'nowrap'
                      }}
                      onMouseEnter={(e) => {
                        if (!relationLoading && selectedRelatedTask) {
                          e.target.style.backgroundColor = '#2563eb';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!relationLoading && selectedRelatedTask) {
                          e.target.style.backgroundColor = '#3b82f6';
                        }
                      }}
                    >
                      {relationLoading ? 'Agregando...' : '+ Agregar'}
                    </button>
                  </div>
                  {relationError && (
                    <p style={{ color: '#dc2626', fontSize: '0.75rem', marginTop: '0.5rem' }}>
                      {relationError}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Responses Section - Only show when editing an existing task */}
          {task && task.id && (
            <div style={{
              marginBottom: '1.5rem',
              marginTop: '1.5rem',
              paddingTop: '1.5rem',
              borderTop: '2px solid #e5e7eb'
            }}>
              <h4 style={{
                fontSize: '1rem',
                fontWeight: 'bold',
                color: '#374151',
                marginBottom: '1rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <span>💬</span> Comentarios ({responses.length})
              </h4>

              {/* Responses List */}
              <div style={{
                marginBottom: '1rem',
                maxHeight: '300px',
                overflowY: 'auto'
              }}>
                {responses.length === 0 ? (
                  <div style={{
                    padding: '1rem',
                    backgroundColor: '#f9fafb',
                    border: '1px dashed #d1d5db',
                    borderRadius: '0.375rem',
                    textAlign: 'center',
                    color: '#6b7280',
                    fontSize: '0.875rem'
                  }}>
                    No hay comentarios aún. ¡Sé el primero en comentar!
                  </div>
                ) : (
                  responses.map((response, index) => (
                    <div
                      key={response.id}
                      style={{
                        padding: '0.75rem',
                        backgroundColor: response.is_agent ? '#f3f4f6' : '#fff',
                        border: '1px solid #e5e7eb',
                        borderRadius: '0.375rem',
                        marginBottom: index < responses.length - 1 ? '0.75rem' : '0'
                      }}
                    >
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        marginBottom: '0.5rem'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          {response.is_agent ? (
                            <>
                              <span style={{ fontSize: '1rem' }}>🤖</span>
                              <span style={{
                                fontSize: '0.875rem',
                                fontWeight: 'bold',
                                color: '#7c3aed'
                              }}>
                                {response.author}
                              </span>
                            </>
                          ) : (
                            <>
                              <span style={{ fontSize: '1rem' }}>👤</span>
                              <span style={{
                                fontSize: '0.875rem',
                                fontWeight: 'bold',
                                color: '#374151'
                              }}>
                                {response.author}
                              </span>
                            </>
                          )}
                        </div>
                        <span style={{
                          fontSize: '0.75rem',
                          color: '#6b7280',
                          fontFamily: 'monospace'
                        }}>
                          {new Date(response.created_at).toLocaleString('es-ES', {
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                      </div>
                      <p style={{
                        margin: 0,
                        fontSize: '0.875rem',
                        color: '#374151',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word'
                      }}>
                        {response.content}
                      </p>
                    </div>
                  ))
                )}
              </div>

              {/* New Response Form */}
              <div>
                <div style={{ marginBottom: '0.75rem' }}>
                  <label style={{
                    display: 'block',
                    color: '#374151',
                    fontSize: '0.875rem',
                    fontWeight: 'bold',
                    marginBottom: '0.5rem'
                  }}>
                    Agregar Comentario
                  </label>
                  <textarea
                    value={newResponse}
                    onChange={(e) => {
                      setNewResponse(e.target.value);
                      setResponseError('');
                    }}
                    rows="3"
                    placeholder="Escribe tu comentario aquí..."
                    disabled={responseLoading}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: responseError ? '1px solid #dc2626' : '1px solid #d1d5db',
                      borderRadius: '0.375rem',
                      fontSize: '0.875rem',
                      outline: 'none',
                      resize: 'vertical',
                      minHeight: '80px',
                      transition: 'border-color 0.15s ease-in-out',
                      backgroundColor: responseLoading ? '#f3f4f6' : 'white'
                    }}
                  />
                  {responseError && (
                    <p style={{ color: '#dc2626', fontSize: '0.75rem', marginTop: '0.25rem' }}>
                      {responseError}
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={handleResponseSubmit}
                  disabled={responseLoading || !newResponse.trim()}
                  style={{
                    padding: '0.5rem 1rem',
                    backgroundColor: (responseLoading || !newResponse.trim()) ? '#d1d5db' : '#3b82f6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '0.375rem',
                    fontSize: '0.875rem',
                    fontWeight: 'bold',
                    cursor: (responseLoading || !newResponse.trim()) ? 'not-allowed' : 'pointer',
                    textTransform: 'uppercase',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                >
                  {responseLoading ? (
                    <>
                      <span style={{
                        display: 'inline-block',
                        width: '1rem',
                        height: '1rem',
                        border: '2px solid #ffffff',
                        borderTopColor: 'transparent',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }}></span>
                      Enviando...
                    </>
                  ) : (
                    <>
                      💬 Agregar Comentario
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Buttons */}
          <div style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '0.75rem',
            paddingTop: '1rem',
            borderTop: '1px solid #e5e7eb'
          }}>
            <button
              type="button"
              onClick={handleCancel}
              disabled={loading}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: 'transparent',
                color: '#6b7280',
                border: '1px solid #d1d5db',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                fontWeight: 'bold',
                cursor: loading ? 'not-allowed' : 'pointer',
                textTransform: 'uppercase',
                opacity: loading ? 0.5 : 1
              }}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: loading ? '#9ca3af' : '#10b981',
                color: 'white',
                border: 'none',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                fontWeight: 'bold',
                cursor: loading ? 'not-allowed' : 'pointer',
                textTransform: 'uppercase',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              {loading ? (
                <>
                  <span style={{ 
                    display: 'inline-block',
                    width: '1rem',
                    height: '1rem',
                    border: '2px solid #ffffff',
                    borderTopColor: 'transparent',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}></span>
                  Guardando...
                </>
              ) : (
                <>
                  {task ? 'Actualizar' : 'Crear'} Tarea
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
    </>,
    document.body
  );
}

CardTaskForm.propTypes = {
  task: PropTypes.object,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
};